import { getFormattedIdv, Idv } from "@components/IdvContainer";
import IdvSlider from "@components/IdvContainer/slider";
import IdvInputBox from "@components/IdvContainer/slider/inputBox";
import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { CardHeadingText, Container, InfoButton } from "./styles";
import { Flex } from "@acko-tech/building-blocks.ui.common";
import { connect, useDispatch, useSelector } from "react-redux";
import { setPayloadData } from "@store/slices/globalState";
import { getWidgetSpecificProps, IdvOPTIONS } from "@utils/index";
import { setAnalytics } from "@actions/setAnalyticsData";
import { CommonProps } from "@utils/interface";
import { useRouter } from "next/router";

const mapStateToProps = function (state: any, ownProps: any) {
    return getWidgetSpecificProps(
        state.configuration?.normalisedConfig,
        ownProps.id
    );
};

interface IDVContainerWithSliderProps extends CommonProps {
    idv: Idv;
    cardHeading: string;
    infoCTA: string;
    styles?: {
        inputBox?: React.CSSProperties;
        slider?: React.CSSProperties;
    };
    mStyles?: object;
    showRecommendedRange?: boolean;
    dWebStyles?: object;
}

const IDVContainerWithSlider: React.FC<IDVContainerWithSliderProps> = ({
    idv,
    cardHeading,
    infoCTA,
    styles = {},
    mStyles,
    dWebStyles,
    showRecommendedRange=false,
    ...props
}) => {
    const { actions, is_visible } = props || {};
    const { on_info_click, on_idv_change } = actions || {};
    const { min, max, current, recommendedIdv } = getFormattedIdv(idv);
    const [selectedValue, setSelectedValue] = useState<number>(current);
    const [sliderInteracted, setSliderInteracted] = useState<boolean>(false);
    const [sliderInteractionValue, setSliderInteractionValue] =
        useState<number>(current);
    const lastSliderSetValue = useRef<number | null>(null);
    const dispatch = useDispatch();

    const hanldeInfoClicked = () => {
        if (on_info_click?.[0] && typeof on_info_click[0].funCall === "function") {
            on_info_click?.forEach((eachFunc: any) => {
                eachFunc.funCall(eachFunc.args);
            });
        }
    };
    const { payloadData, selectedIdvFromPayload } = useSelector(({ globalState }: any) => {
        return {
            payloadData: globalState?.payloadData,
            selectedIdvFromPayload: globalState?.payloadData?.selected_idv
        };
    });

    useEffect(() => {
        const hasUserSelection = selectedIdvFromPayload && selectedIdvFromPayload !== current;
        
        if (!hasUserSelection && !sliderInteracted && current !== selectedValue) {
            setSelectedValue(current);
        }
    }, [current, selectedIdvFromPayload, selectedValue, sliderInteracted]);

    const previousSelectedValue = useRef<number>(current);
    
    useEffect(() => {
        if (selectedValue) {
            if (sliderInteracted) {
                lastSliderSetValue.current = selectedValue;
            }
            if (previousSelectedValue.current !== selectedValue) {
                dispatch(
                    setPayloadData({
                        selected_idv: selectedValue,
                        selected_idv_type: IdvOPTIONS.custom
                    })
                );
                setAnalytics({
                    selected_idv: selectedValue,
                    selected_idv_type: IdvOPTIONS.custom
                });
                previousSelectedValue.current = selectedValue;
            }
        }
    }, [selectedValue, sliderInteracted, dispatch]);
    const router = useRouter();
    const { proposal_ekey, current_node } = router?.query;

    useEffect(() => {
        if (sliderInteracted) {
            return;
        }       
        if (selectedIdvFromPayload === previousSelectedValue.current) {
            return;
        }      
        if (selectedIdvFromPayload && 
            selectedIdvFromPayload !== selectedValue && 
            selectedIdvFromPayload >= min && 
            selectedIdvFromPayload <= max &&
            lastSliderSetValue.current !== selectedIdvFromPayload) {
            setSelectedValue(selectedIdvFromPayload);
            lastSliderSetValue.current = null;
            setSliderInteracted(false);
            previousSelectedValue.current = selectedIdvFromPayload;
        }
    }, [selectedIdvFromPayload, min, max, selectedValue, sliderInteracted]);

    const onSlideEnd = (value: number) => {
        if (on_idv_change?.[0] && typeof on_idv_change[0].funCall === "function") {
            on_idv_change?.forEach((eachFunc: any) => {
                const payload = {
                    requestBody: {
                        current_node: current_node,
                        expected_node: "",
                        journey: "fresh_car"
                    },
                    data: {
                        proposal_ekey: proposal_ekey,
                        ...eachFunc?.args?.data,
                        ...payloadData,
                        selected_idv: value,
                        selected_idv_type: IdvOPTIONS.custom
                    }
                };
                eachFunc.funCall({ ...eachFunc.args, ...payload });
            });
        }
    };

    if (!is_visible) {
        return null;
    }

    return (
        <Container mStyles={mStyles} dWebStyles={dWebStyles}>
            <Flex justifyContent="space-between">
                <CardHeadingText>{cardHeading}</CardHeadingText>
                <InfoButton onClick={hanldeInfoClicked}>{infoCTA}</InfoButton>
            </Flex>
            <IdvInputBox
                selectedIdv={selectedValue}
                setSelectedIdv={setSelectedValue}
                minIdv={min}
                maxIdv={max}
                recommendedIdv={recommendedIdv}
                styles={styles.inputBox}
                onInputEnd={onSlideEnd}
            />
            <IdvSlider
                min={min}
                max={max}
                selectedValue={selectedValue}
                setSelectedValue={setSelectedValue}
                setSliderInteracted={setSliderInteracted}
                singleRow={true}
                threshHold={recommendedIdv}
                setSliderInteractionValue={setSliderInteractionValue}
                styles={styles.slider}
                onSlideEnd={onSlideEnd}
                showRecommendedRange={showRecommendedRange}
            />
        </Container>
    );
};

export default connect(mapStateToProps)(IDVContainerWithSlider);